import React from 'react'
import { Button } from '@blueprintjs/core';
import { Suggest } from '@blueprintjs/select';
import { observable, action, toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Api } from 'services/Api';
import axios from 'axios';
import debounce from 'services/debounce';

// Pick a work order by number. The menu matches the supplier picker's
// (SupplierAutocomplete; styles .ps-menu): what it found, then each work
// order with its customer.

export const WorkRow = ({ work, active, handleClick }) => {
  const a = work.attributes || {};
  const client = a.client || {};
  return (
    <li
      key={work.id}
      role="option"
      aria-selected={active}
      className={`ps-row${active ? ' is-active' : ''}`}
      onMouseDown={(event) => event.preventDefault()}
      onClick={handleClick}
    >
      <div className="ps-row__main">
        <span className="ps-row__name">{a.number}</span>
        {client.name && <span className="ps-row__description">{client.name}</span>}
      </div>
    </li>
  );
};

@observer
class WorkReportAutocomplete extends React.Component {

  @observable loading = false;
  @observable query = '';
  @observable failed = false;
  @observable results = [];
  @observable selectedWork = null;
  request = 0;

  componentDidUpdate(){
    if(this.props.value){
      this.selectedWork = this.props.value;
    }
  }

  componentDidMount() {
   
    let {
      selectedWorkId
    } = this.props;
    selectedWorkId = selectedWorkId;
     
    if (selectedWorkId) {
      let workReportSearch = {
        where: {
          client_id: '',
          location_id:'',
          id:selectedWorkId,
          kind: 'WorkReport'
        },
        order: { created_at: 'desc' },
        per_page: 25,
        page: 1
      }

      let promise = Api.post('/work_reports/search', { search:workReportSearch})
      promise.then(response => {
        this.selectedWork = response.data.data[0];
      })
    }
    if(this.props.value){
      this.selectedWork = this.props.value;
    }
  }

  search = debounce((query) => this.onQueryChange(query), 250);

  @action
  onQueryInput = (query) => {
    this.query = query;
    this.loading = query.trim() !== '';
    this.failed = false;
    this.search(query);
  };

  @action
  onQueryChange(query) {
    this.query = query;
    this.failed = false;
    if (query.trim() === '') {
      this.request++;
      this.loading = false;
      this.results = [];
      return;
    }
    this.loading = true;
    let workReportSearch = {
      where: {
        client_id: '',
        location_id:'',
        number:query.trim(),
        kind: 'WorkReport'
      },
      order: { created_at: 'desc' },
      per_page: 25,
      page: 1
    }

    
    // Only the latest search's answer counts.
    const id = ++this.request;
    let promise = Api.post('/work_reports/search', { search:workReportSearch})
    promise
      .then((response) => { if (id === this.request) this.onWorksLoad(response); })
      .catch((error) => { if (id === this.request) this.onWorksLoadFail(error); })
    return promise;
  }

  @action
  onWorksLoadFail(error) {
    if (axios.isCancel(error)) {
      this.loading = true;
    } else {
      this.loading = false;
      this.failed = true;
    }
  }

  @action
  onWorksLoad(response) {
    this.loading = false;
    this.failed = false;
    this.results = response.data.data;
  }

  @action
  onWorkReportSelect(work) {
    this.selectedWork = work;
    this.props.onWorkReportSelect(work);
  }

  @action
  clearSelectedWork() {
    this.selectedWork = null;
    this.props.onWorkReportSelect(null);
  }

  renderMenu = ({ items, itemsParentRef, renderItem }) => {
    const typed = this.query.trim();
    const rows = items.map(renderItem).filter(Boolean);
    let status;
    if (!typed) status = 'Type a work order number';
    else if (this.loading) status = `Searching for “${typed}”…`;
    else if (this.failed) status = "Couldn't load work orders. Check your connection and type again.";
    else if (rows.length) status = `${rows.length} ${rows.length === 1 ? 'work order matches' : 'work orders match'} “${typed}”`;
    else status = `No work order matches “${typed}”. Check the number and try again.`;
    return (
      <div className="ps-menu ps-menu--narrow">
        <div className="ps-menu__status" role="status">{status}</div>
        {typed && rows.length > 0 && !this.loading && (
          <ul className="ps-menu__list" ref={itemsParentRef} role="listbox" aria-label="Work orders">
            {rows}
          </ul>
        )}
      </div>
    );
  };

  render() {
    const { inputProps } = this.props;
    const selected = this.selectedWork;
    if (selected) {
      const number = selected.attributes.number;
      return (
        <div>
          <Button
            text={number}
            rightIcon="cross"
            className="bp3-text-overflow"
            style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}
            title={`${number}: click to pick another`}
            onClick={this.clearSelectedWork.bind(this)}
          />
        </div>
      );
    }
    // The menu is drawn inside Suggest, so read its state here to redraw it
    // when a search finishes or fails without the results changing.
    const { loading, failed } = this;
    return (
      <div>
        <Suggest
          inputProps={{ placeholder: 'Search work orders', ...inputProps }}
          closeOnSelect
          openOnKeyDown={false}
          resetOnQuery
          resetOnSelect
          query={this.query}
          popoverProps={{ minimal: true, usePortal: true, popoverClassName: 'ps-menu-popover' }}
          onQueryChange={this.onQueryInput}
          items={toJS(this.results)}
          itemRenderer={(work, { handleClick, modifiers }) => (
            <WorkRow key={work.id} work={work} active={modifiers.active} handleClick={handleClick} />
          )}
          itemListRenderer={(props) => this.renderMenu({ ...props, loading, failed })}
          inputValueRenderer={(work) => work.attributes.number}
          onItemSelect={this.onWorkReportSelect.bind(this)}
          noResults={null}
        />
      </div>
    );
  }
}

WorkReportAutocomplete.propTypes = {
  inputProps: PropTypes.object,
  onWorkReportSelect: PropTypes.func.isRequired
}

export default WorkReportAutocomplete;